import { create } from 'zustand';
import { ApiService } from '@/shared/services/api.service';
import { isCurrentUserGuest } from '@/shared/utils/guest-detection.util';
import { SUPPORTED_LANGUAGES } from '@/shared/services/translation.service';

// Debounce timer
let saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_DELAY = 5000; // 5 second delay

export type TranscriptionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';
export type CaptionTextSize = 'small' | 'medium' | 'large' | 'extra-large';
export type TranslationLanguage = keyof typeof SUPPORTED_LANGUAGES;

export interface TranscriptEntry {
  id: string;
  text: string;
  translatedText?: string;
  isTranslating?: boolean;
  confidence: number;
  isFinal: boolean;
  speaker?: string;
  timestamp: number;
  roomPid: string;
}

export interface GroupedTranscript {
  speaker: string;
  transcripts: Array<{
    id: string;
    text: string;
    translatedText?: string;
    isTranslating?: boolean;
    timestamp: number;
  }>;
  firstTimestamp: number;
}

export interface LiveCaptionsState {
  isEnabled: boolean;
  transcriptionStatus: TranscriptionStatus;
  transcripts: TranscriptEntry[];
  currentTranscript: string;
  textSize: CaptionTextSize;
  isLoadingSettings: boolean;
  translationEnabled: boolean;
  targetLanguage: TranslationLanguage;
  showBottomCaptions: boolean;

  setIsEnabled: (enabled: boolean) => void;
  toggleEnabled: () => void;
  setTranscriptionStatus: (status: TranscriptionStatus) => void;
  addTranscript: (transcript: TranscriptEntry) => void;
  updateCurrentTranscript: (text: string) => void;
  clearTranscripts: () => void;
  getRecentTranscripts: (limit?: number) => TranscriptEntry[];
  getGroupedTranscripts: (limit?: number) => GroupedTranscript[];
  setTextSize: (size: CaptionTextSize) => void;
  setTranslationEnabled: (enabled: boolean) => void;
  setTargetLanguage: (language: TranslationLanguage) => void;
  updateTranscriptTranslation: (id: string, translatedText: string) => void;
  setTranscriptTranslating: (id: string, isTranslating: boolean) => void;
  setShowBottomCaptions: (show: boolean) => void;
  loadSettings: () => Promise<void>;
  saveSettings: () => void;
  /** Controls the "Catch Me Up" modal visibility; kept in store so BottomLiveCaptions can open it. */
  catchMeUpOpen: boolean;
  setCatchMeUpOpen: (open: boolean) => void;
}

export const useLiveCaptionsStore = create<LiveCaptionsState>((set, get) => ({
  isEnabled: false,
  transcriptionStatus: 'idle',
  transcripts: [],
  currentTranscript: '',
  textSize: 'medium',
  isLoadingSettings: false,
  translationEnabled: false,
  targetLanguage: 'original',
  showBottomCaptions: false,
  catchMeUpOpen: false,

  setIsEnabled: (enabled: boolean) => {
    set({ isEnabled: enabled });
  },

  toggleEnabled: () => {
    const currentState = get().isEnabled;
    set({ isEnabled: !currentState });
  },

  setTranscriptionStatus: (status: TranscriptionStatus) => {
    set({ transcriptionStatus: status });
  },

  setTextSize: (size: CaptionTextSize) => {
    set({ textSize: size });
    // Trigger debounced save
    get().saveSettings();
  },

  setTranslationEnabled: (enabled: boolean) => {
    set({ translationEnabled: enabled });
  },

  setTargetLanguage: (language: TranslationLanguage) => {
    set({ targetLanguage: language });
  },

  updateTranscriptTranslation: (id: string, translatedText: string) => {
    set((state) => ({
      transcripts: state.transcripts.map((t) =>
        t.id === id ? { ...t, translatedText, isTranslating: false } : t
      ),
    }));
  },

  setTranscriptTranslating: (id: string, isTranslating: boolean) => {
    set((state) => ({
      transcripts: state.transcripts.map((t) => (t.id === id ? { ...t, isTranslating } : t)),
    }));
  },

  setShowBottomCaptions: (show: boolean) => {
    set({ showBottomCaptions: show });
    get().saveSettings();
  },
  setCatchMeUpOpen: (open: boolean) => set({ catchMeUpOpen: open }),

  loadSettings: async () => {
    // Only load if not already loaded and not currently loading
    if (get().isLoadingSettings) return;

    set({ isLoadingSettings: true });
    try {
      const resp = await ApiService.get<any>('user-org-preferences/app-settings/live-captions');
      if (resp && resp.settings) {
        const updates: Partial<LiveCaptionsState> = {};
        if (resp.settings.textSize) {
          updates.textSize = resp.settings.textSize as CaptionTextSize;
        }
        if (resp.settings.showBottomCaptions !== undefined) {
          updates.showBottomCaptions = resp.settings.showBottomCaptions;
        }
        set(updates);
      }
    } catch (error) {
      console.error('[LiveCaptionsStore] Failed to load settings:', error);
    } finally {
      set({ isLoadingSettings: false });
    }
  },

  saveSettings: () => {
    // A guest's preferences stay in this session only. The preferences endpoint resolves a
    // guest to their host's row, so persisting here would overwrite the host's own caption
    // settings — and the PUT is platform-user only anyway.
    if (isCurrentUserGuest()) return;

    // Clear existing timer
    if (saveDebounceTimer) {
      clearTimeout(saveDebounceTimer);
    }

    // Set new debounced timer
    saveDebounceTimer = setTimeout(async () => {
      try {
        const { textSize, showBottomCaptions } = get();
        // Save textSize and showBottomCaptions - translation settings are ephemeral to save costs
        await ApiService.put('user-org-preferences/app-settings/live-captions', {
          settings: { textSize, showBottomCaptions },
        });
      } catch (error) {
        console.error('[LiveCaptionsStore] Failed to save settings:', error);
      }
    }, DEBOUNCE_DELAY);
  },

  addTranscript: (transcript: TranscriptEntry) => {
    set((state) => {
      // Check for duplicates based on ID
      const isDuplicateById = state.transcripts.some((t) => t.id === transcript.id);

      if (isDuplicateById) {
        console.debug(
          '[LiveCaptionsStore] Duplicate transcript detected by ID, skipping:',
          transcript.id
        );
        return state; // Return unchanged state
      }

      // Additional duplicate check: same speaker, same text, within 1 second
      const recentDuplicate = state.transcripts.find(
        (t) =>
          t.speaker === transcript.speaker &&
          t.text === transcript.text &&
          Math.abs(t.timestamp - transcript.timestamp) < 1000 // Within 1 second
      );

      if (recentDuplicate) {
        console.debug(
          '[LiveCaptionsStore] Duplicate transcript detected by content/time, skipping:',
          {
            id: transcript.id,
            text: transcript.text.substring(0, 50),
            speaker: transcript.speaker,
          }
        );
        return state; // Return unchanged state
      }

      // No duplicate found, add the transcript
      if (transcript.isFinal) {
        // Final transcript: add to history and clear current
        return {
          transcripts: [...state.transcripts, transcript],
          currentTranscript: '',
        };
      } else {
        // Non-final transcript: don't add to history, just update current
        return {
          ...state,
          currentTranscript: transcript.text,
        };
      }
    });
  },

  updateCurrentTranscript: (text: string) => {
    // Skip no-op updates — zustand notifies subscribers on every set(), and
    // caption providers (Webex/AssemblyAI) re-send the same interim text often.
    if (text === get().currentTranscript) return;
    set({ currentTranscript: text });
  },

  clearTranscripts: () => {
    set({ transcripts: [], currentTranscript: '' });
  },

  getRecentTranscripts: (limit = 10) => {
    const { transcripts } = get();
    return transcripts
      .filter((t) => t.isFinal)
      .slice(-limit)
      .sort((a, b) => a.timestamp - b.timestamp);
  },

  getGroupedTranscripts: (limit = 10) => {
    const { transcripts } = get();
    const finalTranscripts = transcripts
      .filter((t) => t.isFinal)
      .slice(-limit)
      .sort((a, b) => a.timestamp - b.timestamp);

    const grouped: GroupedTranscript[] = [];

    finalTranscripts.forEach((transcript) => {
      const speaker = transcript.speaker || 'Unknown Speaker';
      const lastGroup = grouped[grouped.length - 1];

      // If the last group is from the same speaker, add to it
      if (lastGroup && lastGroup.speaker === speaker) {
        lastGroup.transcripts.push({
          id: transcript.id,
          text: transcript.text,
          translatedText: transcript.translatedText,
          isTranslating: transcript.isTranslating,
          timestamp: transcript.timestamp,
        });
      } else {
        // Create a new group for this speaker
        grouped.push({
          speaker,
          transcripts: [
            {
              id: transcript.id,
              text: transcript.text,
              translatedText: transcript.translatedText,
              isTranslating: transcript.isTranslating,
              timestamp: transcript.timestamp,
            },
          ],
          firstTimestamp: transcript.timestamp,
        });
      }
    });

    return grouped;
  },
}));
